import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import Tabs, { TabsProps } from "@mui/material/Tabs";
import Tab, { TabProps } from "@mui/material/Tab";
import { useThrottledOnScroll } from "../../effects/useThrottledOnScroll";
import { Box, styled } from "@mui/material";

const tabHeight = 140;

const StyledTabs = styled(Tabs)({
  "& .MuiTabs-indicator": {
    backgroundColor: "var(--dmg-primary)",
  },
});

const StyledTab = styled((props: TabProps) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    minWidth: 160,
    textTransform: "none",
    height: 5,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(15),
    "&:focus": {
      opacity: 1,
    },
  })
);

const classes = {
  root: {
    flexGrow: 1,
  },
  indicator: {
    padding: 0,
  },
  stickyNav: {
    backgroundColor: "var(--dmg-bg)",
    position: "fixed",
    top: "64px", // add 64px from top header
    width: "100%",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  },
  stickyNavPosition: {
    position: "absolute",
    top: 0,
    zIndex: 2,
    boxShadow: 2,
  },
  stickyNavWrapper: {
    clipPath: "inset(0 0 0 0);",
  },
};

const makeUnique = (hash: string, unique: {}, i = 1): string => {
  const uniqueHash = i === 1 ? hash : `${hash}-${i}`;
  let uniq: boolean = unique[uniqueHash as keyof {}];
  if (!uniq) {
    uniq = true;
    return uniqueHash;
  }

  return makeUnique(hash, unique, i + 1);
};

const textToHash = (label: string, unique = {}) => {
  return makeUnique(
    encodeURI(
      label
        .toLowerCase()
        .replace(/=&gt;|&lt;| \/&gt;|<code>|<\/code>|&#39;/g, "")
        .replace(/[!@#\$%\^&\*\(\)=_\+\[\]{}`~;:'"\|,\.<>\/\?\s]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
    ),
    unique
  );
};

export interface ItemTab {
  icon?: string;
  label: string;
  component: JSX.Element;
  hash?: string;
  node?: HTMLElement;
}

interface ScrollSpyTabsProps {
  tabsInScroll: ItemTab[];
  heightToContentSection?: number;
}

function ScrollSpyTabs(props: ScrollSpyTabsProps) {
  const [activeState, setActiveState] = useState<string | null>(null);
  const { tabsInScroll, heightToContentSection = 200 } = props;

  const stickyNavRef = useRef<HTMLDivElement>(null);
  const itemsClientRef = useRef<ItemTab[]>([]);
  const clickedRef = useRef(false);
  const unsetClickedRef = useRef<null | ReturnType<typeof setTimeout>>(null);
  const [isNavVisible, setIsNavVisible] = useState(false);

  // change stickyNav width depends on its parent div
  const stickyNavWidth = stickyNavRef.current?.offsetWidth;

  const itemsServer: ItemTab[] = tabsInScroll.map((tab) => {
    const hash = textToHash(tab.label);
    return {
      icon: tab.icon || "",
      label: tab.label,
      component: tab.component,
      hash,
      node: document.getElementById(hash) as HTMLElement,
    };
  });

  useEffect(() => {
    itemsClientRef.current = itemsServer;
  }, [itemsServer]);

  const findActiveIndex = useCallback(() => {
    // set default if activeState is null
    if (activeState === null) setActiveState(itemsServer[0].hash as string);

    // Don't set the active index based on scroll if a link was just clicked
    if (clickedRef.current) return;

    let active;
    const scrollMainContent = document.getElementById("main-content");
    for (let i = itemsClientRef.current.length - 1; i >= 0; i -= 1) {
      // No hash if we're near the top of the page
      if (scrollMainContent.scrollTop < 0) {
        active = { hash: null };
        break;
      }

      const item = itemsClientRef.current[i];

      if (
        item.node &&
        item.node.offsetTop <
        scrollMainContent.scrollTop +
        scrollMainContent.clientHeight / 8 +
        10
      ) {
        active = item;
        break;
      }
    }

    if (active && activeState !== active.hash) {
      setActiveState(active.hash as string);
    }
  }, [activeState, itemsServer]);

  // Corresponds to 10 frames at 60 Hz
  useThrottledOnScroll(itemsServer.length > 0 ? findActiveIndex : null, 166);

  const handleClick = (hash: string) => () => {
    // Used to disable findActiveIndex if the page scrolls due to a click
    clickedRef.current = true;
    unsetClickedRef.current = setTimeout(() => {
      clickedRef.current = false;
    }, 1000);

    if (activeState !== hash) {
      setActiveState(hash);
      const scrollMainContent = document.getElementById("main-content");
      if (scrollMainContent) {
        const element = document.getElementById(hash);
        if (element) {
          scrollMainContent.scrollTo({
            top:
              element.getBoundingClientRect().top +
              scrollMainContent.scrollTop -
              tabHeight, // correct position to headding section
            behavior: "smooth",
          });
        }
      }
    }
  };

  useEffect(
    () => () => {
      window.clearTimeout(
        unsetClickedRef.current as ReturnType<typeof setTimeout>
      );
    },
    []
  );

  // handle show/hide TabsNav
  const handleActiveTabsNav = (e: Event) => {
    setIsNavVisible(
      document.getElementById("main-content").scrollTop > heightToContentSection
    );
  };

  useEffect(() => {
    const scrollMainContent = document.getElementById("main-content");
    scrollMainContent?.addEventListener("scroll", handleActiveTabsNav);
    return () => {
      scrollMainContent.removeEventListener("scroll", handleActiveTabsNav);
    };
  }, []);

  return (
    <Box sx={classes.stickyNavWrapper} {...{ ref: stickyNavRef }}>
      <Box
        sx={classes.stickyNavPosition}
        style={{ display: isNavVisible ? "block" : "none" }}
      >
        <Box sx={classes.stickyNav} style={{ width: stickyNavWidth }}>
          <StyledTabs
            value={activeState ? activeState : itemsServer[0].hash}
            variant="scrollable"
            scrollButtons
          >
            {itemsServer.map((item) => (
              <StyledTab
                key={item.hash}
                label={item.label}
                onClick={handleClick(item.hash as string)}
                value={item.hash}
              />
            ))}
          </StyledTabs>
          <Box sx={classes.indicator} />
        </Box>
      </Box>

      <Box>
        {itemsServer.map((item) => (
          <Box id={item.hash} key={item.label}>
            {item.component}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default memo(ScrollSpyTabs);
